import { HTMLAttributes, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { formatNumber } from 'utils/numberFormat';
import { Tooltip } from 'antd';
import styled from '@emotion/styled';
import { DownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import Fold from './Fold';
import { DetailItem, Amount, Detail, priceRender } from './TradeDetailPaymentLazada';
import { LIHeaderChild } from './LIHeaderChild';

export interface TradeDetailPaymentTikTokProps extends HTMLAttributes<HTMLElement> {
  trade: Conversation.Trade.Vo;
  defaultFold?: boolean;
}

export const TradeDetailPaymentTikTok = (props: TradeDetailPaymentTikTokProps) => {
  const { trade, defaultFold = false, className, ...otherProps } = props;
  const { unit, buyerPayment } = trade;

  const { t } = useTranslate();
  const [isFold, setIsFold] = useState(defaultFold);

  const total = priceRender(buyerPayment?.buyerTotalAmount, (val) => {
    return (
      <DetailItem
        key="total"
        onClick={() => setIsFold((isFold) => !isFold)}
        style={
          className === 'tradeDetail'
            ? { border: '1px solid #F0F2F5', padding: '0 16px', lineHeight: '40px' }
            : {}
        }
      >
        <span style={className === 'tradeDetail' ? { fontWeight: 'bold' } : {}}>
          {t('domain.order.payment.field.buyerTotal')}
        </span>
        {className !== 'tradeDetail' ? (
          <Amount isfold={isFold}>
            <div>{`${unit} ${formatNumber(val, '0,0.00')}`}</div>
            <DownOutlined />
          </Amount>
        ) : null}
      </DetailItem>
    );
  });

  const detail = (
    <Detail
      key="detail"
      style={
        className === 'tradeDetail'
          ? { background: 'none', border: '1px solid #F0F2F5', borderTop: 'none' }
          : {}
      }
    >
      {priceRender(buyerPayment?.itemPriceAfterDiscount, (val) => {
        return (
          <li>
            <dt>{t('domain.order.payment.field.productSubtotal')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(buyerPayment?.itemPrice, (val) => {
        return (
          <LIHeaderChild>
            <dt>{t('domain.order.payment.field.productTotal')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
      {priceRender(buyerPayment?.voucherFromSeller, (val) => {
        return (
          <LIHeaderChild>
            <dt>{t('domain.order.payment.field.sellerDiscount')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
      {priceRender(buyerPayment?.voucherFromPlatform, (val) => {
        return (
          <LIHeaderChild>
            <dt>{t('domain.order.payment.field.platformDiscount')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
      {priceRender(buyerPayment?.buyerPaidShippingFee, (val) => {
        return (
          <li>
            <dt>
              {t('domain.order.payment.field.shippingSubtotal')}
              <Tooltip title={t('scene.chatWorkspace.orderTab.payment.tooltip.shippingSubtotal')}>
                <What />
              </Tooltip>
            </dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(buyerPayment?.originalShippingFee, (val) => {
        return (
          <LIHeaderChild>
            <dt>{t('domain.order.payment.field.originalShippingFee')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
      {priceRender(buyerPayment?.shippingFeeSellerDiscount, (val) => {
        return (
          <LIHeaderChild>
            <dt>{t('domain.order.payment.field.sellerShippingDiscount')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
      {priceRender(buyerPayment?.shippingFeePlatformDiscount, (val) => {
        return (
          <LIHeaderChild>
            <dt>{t('domain.order.payment.field.platformShippingDiscount')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </LIHeaderChild>
        );
      })}
      {priceRender(buyerPayment?.taxes, (val) => {
        return (
          <li>
            <dt>{t('domain.order.payment.field.taxesAndFees')}</dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(buyerPayment?.smallOrderFee, (val) => {
        return (
          <li>
            <dt>
              {t('domain.order.payment.field.smallOrderFee')}
              <Tooltip title={t('scene.chatWorkspace.orderTab.payment.tooltip.smallOrderFee')}>
                <What />
              </Tooltip>
            </dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </li>
        );
      })}
      {priceRender(buyerPayment?.buyerTotalAmount, (val) => {
        return (
          <li>
            <dt>
              {t('domain.order.payment.field.buyerTotal')}
              <Tooltip title={t('scene.chatWorkspace.orderTab.payment.tooltip.buyerTotal')}>
                <What />
              </Tooltip>
            </dt>
            <dd>{`${unit} ${formatNumber(val, '0,0.00')}`}</dd>
          </li>
        );
      })}
    </Detail>
  );

  return total ? (
    <Fold
      isfold={isFold}
      fixedCount={1}
      nodeList={[total, detail]}
      defaultFixedHeight={32}
      style={className === 'tradeDetail' ? { marginBottom: '16px' } : {}}
      {...otherProps}
    />
  ) : null;
};

const What = styled(QuestionCircleOutlined)`
  margin-left: 4px;
  font-weight: 400;
  cursor: help;
`;
